/**
 * The third trigger of `device-cache.md`'s startup/reconnect cycle: **coming back to the app.**
 *
 * The contract's steps 5–7 had exactly two entry points — a boot (once per signed-in account,
 * guarded by `bootedFor`) and an `online` transition. Neither fires when a *resumed* app returns
 * to the foreground: the page was never torn down, so nothing re-runs, and reception never went
 * away, so nothing reconnects. Switch to another app for an hour, come back, and the gate list and
 * every LED are an hour old with nothing on screen saying so. The staleness is invisible by
 * construction — a restored list looks exactly like a fresh one, which is why `statusPhaseOf`
 * exists for the LED and why nothing existed for the list.
 *
 * **This installs the trigger only. It runs no refresh policy of its own** — the caller hands it
 * `runRefreshProbeCycle`, the identical primitive boot, reconnect and the app bar's button run, so
 * there is one way the cache reconciles and four ways to ask for it.
 *
 * The mechanics follow `pwa/shellUpdate.ts`, which solved this same problem first and for the same
 * event. Two of its guards are load-bearing here and are the easy things to "simplify" away:
 *
 *  - **`visibilitychange` fires in both directions.** Half its firings are a *backgrounding*,
 *    where the answer cannot be acted on and the app may be about to be frozen mid-request.
 *    Hence the `visible` check rather than a bare listener.
 *  - **A resident switches apps constantly.** Without the interval floor, every flick between
 *    SeloGate and the messages app is a `GET /gates` plus a probe sweep per gate — and probes are
 *    rate-limited per caller (D68/D90), so the storm would spend the budget the confirm-poll after
 *    a real open depends on.
 */

/** Matches `shellUpdate.ts`'s floor, for the same event and the same reason. */
export const RESUME_MIN_INTERVAL_MS = 60_000

export interface ResumeRefreshOptions {
  readonly minIntervalMs?: number
  readonly now?: () => number
  /** Injected in tests; the browser globals otherwise. */
  readonly doc?: Pick<Document, 'addEventListener' | 'removeEventListener' | 'visibilityState'>
  readonly win?: Pick<Window, 'addEventListener' | 'removeEventListener'>
}

/**
 * Calls `onResume` when the app returns to the foreground, no more than once per interval.
 * Returns the teardown.
 *
 * `onResume` returns **whether it actually started a cycle**, and only a `true` consumes the
 * interval window. That is not a detail: the caller declines while signed out or offline, and a
 * declined resume that stamped the clock would silently suppress the *next* one — so an app
 * resumed offline, and then resumed again a few seconds later with reception back, would do
 * nothing for the following minute. The `online` transition covers the reconnect case, but only
 * when the browser notices it; there is no reason to disarm this one as well.
 */
export function installResumeRefresh(
  onResume: () => boolean,
  options: ResumeRefreshOptions = {},
): () => void {
  const {
    minIntervalMs = RESUME_MIN_INTERVAL_MS,
    now = () => Date.now(),
    doc = typeof document === 'undefined' ? undefined : document,
    win = typeof window === 'undefined' ? undefined : window,
  } = options
  if (doc === undefined || win === undefined) return () => undefined

  let installed = true
  /**
   * Stamped at install rather than left at zero. The provider mounts and boots in the same
   * moment, and boot runs this exact cycle itself — starting the clock at 0 would make the first
   * app switch after launch re-run a sweep that had just finished.
   */
  let lastAt = now()

  function resumed(): void {
    if (!installed || doc?.visibilityState !== 'visible') return
    if (now() - lastAt < minIntervalMs) return
    if (onResume()) lastAt = now()
  }

  doc.addEventListener('visibilitychange', resumed)
  // A bfcache restore runs no module code and fires no `load`; `persisted === false` is an
  // ordinary navigation, which has just booted the whole cache anyway.
  const onPageShow = (event: PageTransitionEvent): void => {
    if (event.persisted) resumed()
  }
  win.addEventListener('pageshow', onPageShow as EventListener)

  return () => {
    installed = false
    doc.removeEventListener('visibilitychange', resumed)
    win.removeEventListener('pageshow', onPageShow as EventListener)
  }
}
